/**
 * Centralized Logging System
 * 
 * Provides structured logging capabilities for the entire application.
 * Supports different log levels and contexts for better debugging and monitoring.
 */

type LogLevel = 'info' | 'warn' | 'error' | 'debug';

interface LogContext {
  [key: string]: any;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: {
    message: string;
    stack?: string;
  };
}

/**
 * Centralized Logger class
 */
export class Logger {
  private static log(level: LogLevel, message: string, context?: LogContext, error?: Error) {
    const timestamp = new Date().toISOString();
    const logEntry: LogEntry = {
      timestamp,
      level,
      message,
      ...(context && { context }),
      ...(error && {
        error: {
          message: error.message,
          stack: error.stack
        }
      })
    };
    
    if (process.env.NODE_ENV === 'production') {
      // In production, use structured JSON logging
      console.log(JSON.stringify(logEntry));
    } else {
      // In development, use readable format
      const contextStr = context ? ` ${JSON.stringify(context)}` : '';
      const errorStr = error ? `\nError: ${error.message}${error.stack ? `\n${error.stack}` : ''}` : '';
      
      console[level === 'error' ? 'error' : 'log'](
        `[${timestamp}] ${level.toUpperCase()}: ${message}${contextStr}${errorStr}`
      );
    }
  }

  /**
   * Log informational messages
   */
  static info(message: string, context?: LogContext) {
    this.log('info', message, context);
  }

  /**
   * Log warning messages
   */
  static warn(message: string, context?: LogContext) {
    this.log('warn', message, context);
  }

  /**
   * Log error messages
   */
  static error(message: string, error?: Error, context?: LogContext) {
    this.log('error', message, context, error);
  }

  /**
   * Log debug messages (only in development)
   */
  static debug(message: string, context?: LogContext) {
    if (process.env.NODE_ENV !== 'production') {
      this.log('debug', message, context);
    }
  }

  /**
   * Log API requests
   */
  static apiRequest(method: string, url: string, statusCode: number, duration?: number, context?: LogContext) {
    this.info(`API ${method} ${url} - ${statusCode}`, {
      ...context,
      method,
      url,
      statusCode,
      duration
    });
  }

  /**
   * Log database operations
   */
  static database(operation: string, table: string, duration?: number, context?: LogContext) {
    this.info(`Database ${operation} on ${table}`, {
      ...context,
      operation,
      table,
      duration
    });
  }

  /**
   * Log authentication events
   */
  static auth(event: string, userId?: string, context?: LogContext) {
    this.info(`Auth ${event}`, {
      ...context,
      event,
      userId
    });
  }

  /**
   * Log business logic events
   */
  static business(event: string, entity: string, entityId?: string, context?: LogContext) {
    this.info(`Business ${event}`, {
      ...context,
      event,
      entity,
      entityId
    });
  }

  /**
   * Log performance metrics
   */
  static performance(metric: string, value: number, unit: string, context?: LogContext) {
    this.info(`Performance ${metric}`, {
      ...context,
      metric,
      value,
      unit
    });
  }

  /**
   * Log security events
   */
  static security(event: string, severity: 'low' | 'medium' | 'high' | 'critical', context?: LogContext) {
    const level = severity === 'critical' || severity === 'high' ? 'error' : 'warn';
    this.log(level, `Security ${event}`, {
      ...context,
      event,
      severity
    });
  }
}

/**
 * Create a scoped logger for specific modules
 */
export const createScopedLogger = (scope: string) => ({
  info: (message: string, context?: LogContext) => Logger.info(`[${scope}] ${message}`, context),
  warn: (message: string, context?: LogContext) => Logger.warn(`[${scope}] ${message}`, context),
  error: (message: string, error?: Error, context?: LogContext) => Logger.error(`[${scope}] ${message}`, error, context),
  debug: (message: string, context?: LogContext) => Logger.debug(`[${scope}] ${message}`, context),
  apiRequest: (method: string, url: string, statusCode: number, duration?: number, context?: LogContext) => 
    Logger.apiRequest(method, url, statusCode, duration, { ...context, scope }),
  database: (operation: string, table: string, duration?: number, context?: LogContext) => 
    Logger.database(operation, table, duration, { ...context, scope }),
  auth: (event: string, userId?: string, context?: LogContext) => 
    Logger.auth(event, userId, { ...context, scope }),
  business: (event: string, entity: string, entityId?: string, context?: LogContext) => 
    Logger.business(event, entity, entityId, { ...context, scope }),
  performance: (metric: string, value: number, unit: string, context?: LogContext) => 
    Logger.performance(metric, value, unit, { ...context, scope }),
  security: (event: string, severity: 'low' | 'medium' | 'high' | 'critical', context?: LogContext) => 
    Logger.security(event, severity, { ...context, scope })
});

/**
 * Common scoped loggers
 */
export const apiLogger = createScopedLogger('API');
export const dbLogger = createScopedLogger('Database');
export const authLogger = createScopedLogger('Auth');
export const businessLogger = createScopedLogger('Business');
export const securityLogger = createScopedLogger('Security');
